import axios from 'axios';

// API Base URL - verwende immer /api (wird von nginx als Reverse Proxy weitergeleitet)
// In Development: Proxy aus package.json
// In Production: nginx Reverse Proxy
const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 and 403 errors
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const hasToken = localStorage.getItem('token');

      // Only redirect if:
      // 1. We have a token (user was logged in, but token expired/invalid)
      // 2. We're not on login/register page (to avoid redirect loops and preserve error messages)
      if (hasToken && currentPath !== '/login' && currentPath !== '/register') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // If no token and on login page, don't redirect - let the component show the error
    }

    // Handle 403 errors (forbidden - e.g., deactivated dealer account)
    if (error.response?.status === 403) {
      const errorMessage = error.response?.data?.error || '';
      // If it's a deactivated account error, log out the user
      if (errorMessage.includes('deaktiviert') || errorMessage.includes('jannik@elfhalbe.de')) {
        const currentPath = window.location.pathname;
        // Don't redirect if already on login page
        if (currentPath !== '/login') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // Store error message to show on login page
          window.location.href = '/login?error=' + encodeURIComponent(errorMessage);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to get image URL
// Note: Images require authentication, so we'll fetch them via API and convert to blob URL
export const getImageUrl = async (orderId: string): Promise<string> => {
  const token = localStorage.getItem('token');

  try {
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/image`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        // No image exists - this is expected, don't throw error
        return '';
      }
      throw new Error(`Failed to load image: ${response.status}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    // Only log if it's not a 404
    if (error instanceof Error && !error.message.includes('404')) {
      console.error('Error loading image:', error);
    }
    return ''; // Return empty string on error
  }
};

export default api;
